// ---------------------------------------------------------------------------
// F5 (WonBanking arc, 2026-07-27) — Design System PACK release → generation
// token seam.
//
// FS-1 ships immutable Design System pack releases (compileDesignSystemRelease
// → cogo.design_system_pack_records). This module is the minimal consumer that
// lets a Studio generation follow such a release: it verifies a supplied
// release manifest FAIL-CLOSED (content hash + package hash recomputed over
// @cogo/design-data's canonical bytes with Web Crypto, identity pinned to the
// caller's expected releaseId) and projects the release's light-mode ctok:*
// color tokens into the EXACT `designSystemTokens` flat map the generation
// context already consumes (G2 seam: appSpec.designSystemTokens →
// requestAppGeneration → project.tokens → renderer CSS vars).
//
// It deliberately does NOT touch the Design Ontology / library lane
// (studio-library-design-ontology-binding) or the platform release-catalog
// binding (StudioDesignSystemReleaseIdentity): a pack release is a different
// store with different hashes, so its lineage is persisted under its own
// settings key instead of impersonating either existing identity.
// ---------------------------------------------------------------------------

import { designSystemReleaseHashInputs } from '@cogo/design-data/canonical'
import type {
  DesignSystemGenerationContract,
  DesignSystemReleaseManifest,
} from '@cogo/design-data'
import { admitDesignSystemGenerationContract } from '@cogo/design-data/generation-design-system'
import { designSystemGenerationAuthorityFields } from './project-design-system-generation-binding'

export const DESIGN_SYSTEM_PACK_RELEASE_SETTINGS_KEY =
  'designSystemPackRelease' as const

const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/
/** Semantic slot ids projected to renderer CSS-var keys (`ctok:primary` → `primary`). */
const SEMANTIC_TOKEN_ID_PATTERN = /^ctok:[a-z][a-z0-9-]*$/
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/
const CSS_COLOR_FUNCTION_PATTERN =
  /^(?:rgb|rgba|hsl|hsla|oklch|oklab|color)\(/
/**
 * Renderability floor: without these three slots the generated app would
 * render mostly renderer defaults while claiming to follow the pack — that is
 * drift at birth, so the binding refuses instead.
 */
const RENDER_FLOOR_KEYS = ['primary', 'background', 'foreground'] as const

export type DesignSystemPackReleaseBindingErrorCode =
  | 'pack_release_invalid_shape'
  | 'pack_release_reissue_required'
  | 'pack_release_identity_mismatch'
  | 'pack_release_hash_mismatch'
  | 'pack_release_hash_unverifiable'
  | 'pack_release_not_released'
  | 'pack_release_no_web_target'
  | 'pack_release_missing_render_floor'

export class DesignSystemPackReleaseBindingError extends Error {
  override readonly name = 'DesignSystemPackReleaseBindingError'

  constructor(
    readonly code: DesignSystemPackReleaseBindingErrorCode,
    detail?: string,
  ) {
    super(detail ? `${code}: ${detail}` : code)
  }
}

/** Durable pack lineage persisted on the generated project's settings row. */
export interface StudioDesignSystemPackReleaseLineage {
  schemaVersion: 'cogo.studio-design-system-pack-release-lineage.v2'
  source: 'design-system-pack-records'
  packageId: string
  packageVersion: string
  releaseId: string
  releaseContentHash: string
  packageHash: string
  environmentClass: string
  appliedMode: 'light'
  /** Exact applied axes, duplicated for durable project-settings readback. */
  generationDesignSystem: DesignSystemGenerationContract
}

/** A hash-verified, identity-pinned pack release bound for generation. */
export interface StudioDesignSystemPackReleaseBinding {
  schemaVersion: 'cogo.studio-design-system-pack-release-binding.v2'
  packageId: string
  packageVersion: string
  releaseId: string
  releaseContentHash: string
  packageHash: string
  environmentClass: string
  mode: 'light'
  /** Exact six-axis selection verified as part of the immutable release. */
  generationDesignSystem: DesignSystemGenerationContract
  /** Kebab CSS-var keys (renderer COLOR_KEY_TO_CSS names) → CSS color values. */
  designSystemTokens: Record<string, string>
  themeHint: { primaryColor: string; style: string }
  /** ctok:* light color tokens whose values could not be projected (honesty ledger). */
  skippedTokenIds: string[]
  lineage: StudioDesignSystemPackReleaseLineage
}

export interface ExpectedPackReleaseIdentity {
  /** Mandatory pin: the exact immutable release this generation must follow. */
  releaseId: string
  packageId?: string
  packageVersion?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function reject(
  code: DesignSystemPackReleaseBindingErrorCode,
  detail?: string,
): never {
  throw new DesignSystemPackReleaseBindingError(code, detail)
}

function parseManifestShape(value: unknown): DesignSystemReleaseManifest {
  if (!isRecord(value)) reject('pack_release_invalid_shape', 'not an object')
  if (value.schemaVersion === 'cogo.design-system-release-manifest.v1') {
    reject(
      'pack_release_reissue_required',
      'Design System release v1 does not carry the exact generation axes; reissue it through the v2 pack lifecycle before applying it.',
    )
  }
  if (value.schemaVersion !== 'cogo.design-system-release-manifest.v2') {
    reject('pack_release_invalid_shape', 'schemaVersion')
  }
  for (const key of [
    'releaseId',
    'packageId',
    'packageVersion',
    'environmentClass',
    'releasedAt',
  ] as const) {
    if (typeof value[key] !== 'string' || !(value[key] as string).trim()) {
      reject('pack_release_invalid_shape', key)
    }
  }
  for (const key of ['contentHash', 'packageHash'] as const) {
    if (
      typeof value[key] !== 'string' ||
      !CONTENT_HASH_PATTERN.test(value[key] as string)
    ) {
      reject('pack_release_invalid_shape', key)
    }
  }
  const snapshot = value.packageSnapshot
  if (!isRecord(snapshot) || !Array.isArray(snapshot.tokens)) {
    reject('pack_release_invalid_shape', 'packageSnapshot')
  }
  return value as unknown as DesignSystemReleaseManifest
}

async function sha256(input: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    // Never “verify” by skipping: without Web Crypto the binding must refuse.
    reject('pack_release_hash_unverifiable', 'crypto.subtle unavailable')
  }
  const digest = await subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  )
  const bytes = Array.from(new Uint8Array(digest))
  return `sha256:${bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('')}`
}

function hex8ToRgba(value: string): string {
  const r = parseInt(value.slice(1, 3), 16)
  const g = parseInt(value.slice(3, 5), 16)
  const b = parseInt(value.slice(5, 7), 16)
  const alpha = Math.round((parseInt(value.slice(7, 9), 16) / 255) * 1000) / 1000
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Project a color token value into a form the renderer's normalizeTokenColor
 * accepts. `null` = unprojectable (recorded in skippedTokenIds, never thrown
 * unless it breaks the render floor).
 */
function projectColorValue(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim().toLowerCase()
  if (HEX_COLOR_PATTERN.test(trimmed)) {
    return trimmed.length === 9 ? hex8ToRgba(trimmed) : trimmed
  }
  if (CSS_COLOR_FUNCTION_PATTERN.test(trimmed)) return value.trim()
  return null
}

/**
 * Verify a Design System pack release manifest FAIL-CLOSED and bind its
 * light-mode semantic color tokens for generation.
 *
 * Guarantees on success:
 *  - `contentHash` and `packageHash` were RECOMPUTED over the canonical bytes
 *    (@cogo/design-data canonical) and matched — any field or token tamper
 *    rejects with `pack_release_hash_mismatch`.
 *  - identity is pinned: releaseId (and optional packageId/packageVersion)
 *    equal the caller's expectation, and the packageSnapshot triple matches
 *    the manifest triple.
 *  - lifecycleState is 'released' and immutable, the web target is declared,
 *    and the render floor (primary/background/foreground) is projectable.
 *
 * NOT guaranteed (adversarial-review MINOR, 2026-07-27): this function proves
 * internal well-formedness of the SUPPLIED bytes only — the hashes are
 * recomputed over that same manifest, so an internally-consistent fabricated
 * manifest passes. Admission to the server pack store is anchored SOLELY by
 * fetching the release row through the authenticated pack-lifecycle GET; if
 * this seam is ever promoted past the DEV-only trigger, a server-side
 * presence check must be added first.
 */
export async function resolveDesignSystemPackReleaseBinding(
  releaseValue: unknown,
  expected: ExpectedPackReleaseIdentity,
): Promise<StudioDesignSystemPackReleaseBinding> {
  const release = parseManifestShape(releaseValue)

  if (
    release.releaseId !== expected.releaseId ||
    (expected.packageId !== undefined &&
      release.packageId !== expected.packageId) ||
    (expected.packageVersion !== undefined &&
      release.packageVersion !== expected.packageVersion)
  ) {
    reject('pack_release_identity_mismatch', release.releaseId)
  }

  const inputs = designSystemReleaseHashInputs(release)
  const [contentHash, packageHash] = await Promise.all([
    sha256(inputs.contentHashInput),
    sha256(inputs.packageHashInput),
  ])
  if (contentHash !== release.contentHash) {
    reject('pack_release_hash_mismatch', 'contentHash')
  }
  if (packageHash !== release.packageHash) {
    reject('pack_release_hash_mismatch', 'packageHash')
  }

  // Post-hash consistency: these fields are now VERIFIED bytes; the checks
  // below reject releases that are authentic but not bindable.
  if (release.lifecycleState !== 'released' || release.immutable !== true) {
    reject('pack_release_not_released', release.lifecycleState)
  }
  const snapshot = release.packageSnapshot
  if (
    snapshot.releaseId !== release.releaseId ||
    snapshot.packageId !== release.packageId ||
    snapshot.packageVersion !== release.packageVersion
  ) {
    reject('pack_release_identity_mismatch', 'packageSnapshot identity')
  }
  if (!snapshot.targets.includes('web')) {
    reject('pack_release_no_web_target', snapshot.targets.join(','))
  }

  let generationDesignSystem: DesignSystemGenerationContract
  try {
    generationDesignSystem = admitDesignSystemGenerationContract(
      release.generationDesignSystem,
    )
  } catch (error) {
    reject(
      'pack_release_invalid_shape',
      error instanceof Error ? error.message : 'generationDesignSystem',
    )
  }

  const designSystemTokens: Record<string, string> = {}
  const skippedTokenIds: string[] = []
  for (const token of snapshot.tokens) {
    if (token.mode !== 'light') continue
    if (token.tokenType !== 'color') continue
    if (!SEMANTIC_TOKEN_ID_PATTERN.test(token.tokenId)) continue
    const key = token.tokenId.slice('ctok:'.length)
    const projected = projectColorValue(token.value)
    if (projected === null) {
      skippedTokenIds.push(token.tokenId)
      continue
    }
    designSystemTokens[key] = projected
  }
  const missingFloor = RENDER_FLOOR_KEYS.filter(
    (key) => designSystemTokens[key] === undefined,
  )
  if (missingFloor.length > 0) {
    reject('pack_release_missing_render_floor', missingFloor.join(','))
  }

  const lineage: StudioDesignSystemPackReleaseLineage = {
    schemaVersion: 'cogo.studio-design-system-pack-release-lineage.v2',
    source: 'design-system-pack-records',
    packageId: release.packageId,
    packageVersion: release.packageVersion,
    releaseId: release.releaseId,
    releaseContentHash: release.contentHash,
    packageHash: release.packageHash,
    environmentClass: release.environmentClass,
    appliedMode: 'light',
    generationDesignSystem,
  }

  return {
    schemaVersion: 'cogo.studio-design-system-pack-release-binding.v2',
    packageId: release.packageId,
    packageVersion: release.packageVersion,
    releaseId: release.releaseId,
    releaseContentHash: release.contentHash,
    packageHash: release.packageHash,
    environmentClass: release.environmentClass,
    mode: 'light',
    generationDesignSystem,
    designSystemTokens,
    themeHint: {
      primaryColor: designSystemTokens.primary!,
      style: generationDesignSystem.style,
    },
    skippedTokenIds,
    lineage,
  }
}

/**
 * Merge the pack lineage under its own settings key. Like the G1/G3 birth
 * settings helpers, this composes with sibling keys instead of replacing the
 * settings document, so the drift comparator can read the DECLARED identity
 * from `settings.designSystemPackRelease` alongside generation/library keys.
 */
export function withDesignSystemPackReleaseLineage(
  existingSettings: unknown,
  lineage: StudioDesignSystemPackReleaseLineage,
): Record<string, unknown> {
  const settings = isRecord(existingSettings) ? { ...existingSettings } : {}
  settings[DESIGN_SYSTEM_PACK_RELEASE_SETTINGS_KEY] = lineage
  return settings
}

/**
 * Overlay the verified pack tokens as the plan's presentation authority —
 * the same appSpec fields the G2 preset path populates via deriveTheme, so
 * everything downstream (requestAppGeneration token wiring, project.tokens
 * injection, renderer CSS vars) follows without new seams. Non-mutating.
 */
export function withDesignSystemPackReleaseGenerationAuthority<
  T extends { appSpec: object },
>(plan: T, binding: StudioDesignSystemPackReleaseBinding): T {
  const fields = designSystemGenerationAuthorityFields(binding)
  return {
    ...plan,
    appSpec: {
      ...plan.appSpec,
      ...fields,
      generationDesignSystem: { ...binding.generationDesignSystem },
    },
    themeHint: fields.themeHint,
    designSystemTokens: fields.designSystemTokens,
  } as T
}
